import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  User,
  Users,
  Settings,
  Save,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Edit2,
  X,
  Check,
  Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Permission categories and their fields
const SELF_PERMISSION_CATEGORIES = [
  {
    category: 'Personal Information',
    fields: [
      { key: 'personal_basic', label: 'Basic Info', description: 'Name, DOB, SSN, Gender' },
      { key: 'personal_contact', label: 'Contact Info', description: 'Email, Phone, Address' },
      { key: 'personal_social', label: 'Social Links', description: 'LinkedIn, etc.' },
    ]
  },
  {
    category: 'Job Information',
    fields: [
      { key: 'job_info', label: 'Job Details', description: 'Title, Department, Hire Date' },
      { key: 'job_compensation', label: 'Compensation', description: 'Pay Rate, Salary History' },
    ]
  },
  {
    category: 'Time Off',
    fields: [
      { key: 'time_off', label: 'PTO Balance', description: 'View time off balances' },
      { key: 'time_off_request', label: 'PTO Requests', description: 'Submit time off requests' },
    ]
  },
  {
    category: 'Other',
    fields: [
      { key: 'benefits', label: 'Benefits', description: 'Benefits information' },
      { key: 'documents', label: 'Documents', description: 'Personal documents' },
      { key: 'documents_upload', label: 'Upload Documents', description: 'Upload own documents' },
      { key: 'performance', label: 'Performance', description: 'Performance reviews' },
      { key: 'performance_self_assess', label: 'Self Assessment', description: 'Complete self-assessments' },
      { key: 'compliance', label: 'Compliance Training', description: 'Training records' },
      { key: 'emergency_contact', label: 'Emergency Contact', description: 'Emergency contact info' },
      { key: 'notes', label: 'HR Notes', description: 'Notes about employee' },
    ]
  }
];

const OTHERS_PERMISSION_CATEGORIES = [
  {
    category: 'View Other Employees',
    fields: [
      { key: 'personal_basic', label: 'Basic Info', description: 'Name, DOB, Gender' },
      { key: 'personal_contact', label: 'Contact Info', description: 'Email, Phone' },
      { key: 'job_info', label: 'Job Details', description: 'Title, Department' },
      { key: 'job_compensation', label: 'Compensation', description: 'Pay rates (sensitive)' },
      { key: 'time_off', label: 'Time Off', description: 'PTO balances and requests' },
      { key: 'benefits', label: 'Benefits', description: 'Benefits info' },
      { key: 'documents', label: 'Documents', description: 'Employee documents' },
      { key: 'performance', label: 'Performance', description: 'Reviews and goals' },
      { key: 'compliance', label: 'Compliance', description: 'Training records' },
      { key: 'emergency_contact', label: 'Emergency Contact', description: 'Emergency info' },
      { key: 'notes', label: 'HR Notes', description: 'Notes (sensitive)' },
    ]
  }
];

const ADMIN_PERMISSIONS = [
  { key: 'admin_dashboard', label: 'Admin Dashboard', description: 'View admin stats' },
  { key: 'admin_users', label: 'User Management', description: 'Manage user accounts' },
  { key: 'admin_permissions', label: 'Permission Levels', description: 'Manage access levels' },
  { key: 'admin_audit', label: 'Audit Logs', description: 'View audit trail' },
  { key: 'admin_job_duties', label: 'Job Duties', description: 'Manage job duties' },
  { key: 'admin_signatures', label: 'Signature Documents', description: 'E-signature management' },
  { key: 'admin_declinations', label: 'Declinations', description: 'Declination tracking' },
  { key: 'admin_invoices', label: 'Invoice Verification', description: 'Verify invoices' },
  { key: 'admin_integrations', label: 'Integrations', description: 'API integrations' },
];

const NAV_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard' },
  { key: 'personnel', label: 'Personnel', description: 'View all personnel' },
  { key: 'agencies', label: 'Agencies', description: 'Agency management' },
  { key: 'reports', label: 'Reports', description: 'View reports' },
  { key: 'map', label: 'Map', description: 'Coverage map' },
  { key: 'uploads', label: 'Uploads', description: 'Data uploads' },
  { key: 'communications', label: 'Communications', description: 'Email/SMS inbox + Team Board' },
];

const ACCESS_LEVELS = [
  { value: 'none', label: 'No Access', color: 'bg-red-100 text-red-800' },
  { value: 'view', label: 'View', color: 'bg-blue-100 text-blue-800' },
  { value: 'edit', label: 'Edit', color: 'bg-green-100 text-green-800' },
];

// Move AccessSelector outside the main component to prevent recreation on each render
function AccessSelector({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {ACCESS_LEVELS.map(level => (
        <button
          key={level.value}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange(level.value);
          }}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            value === level.value 
              ? level.color + ' font-medium' 
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {level.label}
        </button>
      ))}
    </div>
  );
}

// Move ToggleSwitch outside the main component
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`w-10 h-5 rounded-full transition-colors relative ${
        checked ? 'bg-green-500' : 'bg-gray-300'
      }`}
    >
      <span 
        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function PermissionLevelEditor({ levelId, onSave, onCancel, isNew = false }) {
  const [level, setLevel] = useState({
    name: '',
    description: '',
    is_default: false,
    self_permissions: {},
    others_permissions: {},
    admin_permissions: {},
    nav_permissions: {}
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    self: true,
    others: false,
    admin: false,
    nav: false
  });

  useEffect(() => {
    if (!isNew && levelId) {
      fetchLevel();
    }
  }, [levelId, isNew]);

  const fetchLevel = async () => {
    try {
      const response = await axios.get(`${API}/permission-levels/${levelId}`);
      setLevel(response.data);
    } catch (error) {
      toast.error('Failed to load permission level');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!level.name.trim()) {
      toast.error('Please enter a name for the permission level');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/permission-levels`, level);
        toast.success('Permission level created');
      } else {
        await axios.put(`${API}/permission-levels/${levelId}`, level);
        toast.success('Permission level updated');
      }
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permission level');
    } finally {
      setSaving(false);
    }
  };

  const updateSelfPermission = (key, value) => {
    setLevel(prev => ({
      ...prev,
      self_permissions: { ...prev.self_permissions, [key]: value }
    }));
  };

  const updateOthersPermission = (key, value) => {
    setLevel(prev => ({
      ...prev,
      others_permissions: { ...prev.others_permissions, [key]: value }
    }));
  };

  const updateAdminPermission = (key, value) => {
    setLevel(prev => ({
      ...prev,
      admin_permissions: { ...prev.admin_permissions, [key]: value }
    }));
  };

  const updateNavPermission = (key, value) => {
    setLevel(prev => ({
      ...prev,
      nav_permissions: { ...prev.nav_permissions, [key]: value }
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {isNew ? 'Create Permission Level' : 'Edit Permission Level'}
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure what users with this access level can see and do
          </p>
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              <X size={16} className="mr-1" /> Cancel
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save size={16} className="mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Basic Info */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Name *</label>
            <Input
              value={level.name}
              onChange={(e) => setLevel(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Manager, HR Admin, Employee"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Description</label>
            <Input
              value={level.description || ''}
              onChange={(e) => setLevel(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this access level"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <ToggleSwitch
            checked={level.is_default}
            onChange={(val) => setLevel(prev => ({ ...prev, is_default: val }))}
          />
          <span className="text-sm">Default for new users</span>
        </div>
      </Card>

      {/* Self Permissions */}
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('self')}
          className="w-full p-4 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <User className="text-blue-600" size={20} />
            <span className="font-medium">What Users See About Themselves</span>
            <Badge className="bg-blue-100 text-blue-800 text-xs">Self-Service</Badge>
          </div>
          {expandedSections.self ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {expandedSections.self && (
          <div className="p-4 space-y-6">
            {SELF_PERMISSION_CATEGORIES.map((cat, idx) => (
              <div key={idx}>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">{cat.category}</h4>
                <div className="space-y-2">
                  {cat.fields.map(field => (
                    <div key={field.key} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div>
                        <span className="text-sm font-medium">{field.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">{field.description}</span>
                      </div>
                      <AccessSelector
                        value={level.self_permissions?.[field.key] || 'none'}
                        onChange={(val) => updateSelfPermission(field.key, val)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Others Permissions */}
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('others')}
          className="w-full p-4 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Users className="text-purple-600" size={20} />
            <span className="font-medium">What Users See About Other Employees</span>
            <Badge className="bg-purple-100 text-purple-800 text-xs">Team Access</Badge>
          </div>
          {expandedSections.others ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {expandedSections.others && (
          <div className="p-4 space-y-2">
            {OTHERS_PERMISSION_CATEGORIES[0].fields.map(field => (
              <div key={field.key} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div>
                  <span className="text-sm font-medium">{field.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">{field.description}</span>
                </div>
                <AccessSelector
                  value={level.others_permissions?.[field.key] || 'none'}
                  onChange={(val) => updateOthersPermission(field.key, val)}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Admin Permissions */}
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('admin')}
          className="w-full p-4 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Shield className="text-amber-600" size={20} />
            <span className="font-medium">Admin Console Access</span>
            <Badge className="bg-amber-100 text-amber-800 text-xs">Admin Features</Badge>
          </div>
          {expandedSections.admin ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {expandedSections.admin && (
          <div className="p-4 space-y-2">
            {ADMIN_PERMISSIONS.map(perm => (
              <div key={perm.key} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div>
                  <span className="text-sm font-medium">{perm.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">{perm.description}</span>
                </div>
                <ToggleSwitch
                  checked={level.admin_permissions?.[perm.key] || false}
                  onChange={(val) => updateAdminPermission(perm.key, val)}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Navigation Permissions */}
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('nav')}
          className="w-full p-4 flex items-center justify-between bg-green-50 hover:bg-green-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Settings className="text-green-600" size={20} />
            <span className="font-medium">Navigation Menu Access</span>
            <Badge className="bg-green-100 text-green-800 text-xs">Main Menu</Badge>
          </div>
          {expandedSections.nav ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {expandedSections.nav && (
          <div className="p-4 space-y-2">
            {NAV_PERMISSIONS.map(perm => (
              <div key={perm.key} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div>
                  <span className="text-sm font-medium">{perm.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">{perm.description}</span>
                </div>
                <ToggleSwitch
                  checked={level.nav_permissions?.[perm.key] || false}
                  onChange={(val) => updateNavPermission(perm.key, val)}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
